import { useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { UserPlus } from 'lucide-react'
import {
  TeamMembersTable,
  AddTeamMemberDialog,
  EditMemberRoleDialog,
  RemoveMemberDialog,
  PendingInvitationsTable,
} from '@/components/settings/team'
import {
  useTeamMembers,
  useUpdateMemberRole,
  useRemoveMember,
  useCreateTeamMember,
} from '@/hooks/useTeamMembers'
import { useInvitations, useCancelInvitation } from '@/hooks/useInvitations'
import { useAuth } from '@/contexts'
import type { TeamMember, OrgRole, LocationAssignment } from '@/types/team-members'

export function TeamSettingsPage() {
  const { user } = useAuth()
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false)
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false)
  const [removeMemberDialogOpen, setRemoveMemberDialogOpen] = useState(false)
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null)
  const [cancellingInvitationId, setCancellingInvitationId] = useState<
    string | null
  >(null)

  // Queries
  const { data: members = [], isLoading: membersLoading } = useTeamMembers()
  const { data: invitations = [], isLoading: invitationsLoading } =
    useInvitations()

  // Mutations
  const createTeamMember = useCreateTeamMember()
  const updateMemberRole = useUpdateMemberRole()
  const removeMember = useRemoveMember()
  const cancelInvitation = useCancelInvitation()

  // Check if current user is owner or admin
  const currentMember = members.find((m) => m.user_id === user?.id)
  const isOwnerOrAdmin =
    currentMember?.role === 'owner' ||
    currentMember?.role === 'admin'

  const handleCreateMember = async (data: {
    email: string
    display_name?: string
    role: OrgRole
    location_assignments: LocationAssignment[]
  }) => {
    await createTeamMember.mutateAsync(data)
  }

  const handleEditRole = (member: TeamMember) => {
    setSelectedMember(member)
    setEditRoleDialogOpen(true)
  }

  const handleUpdateRole = async (newRole: OrgRole) => {
    if (!selectedMember) return
    return updateMemberRole.mutateAsync({
      userId: selectedMember.user_id,
      role: newRole,
    })
  }

  const handleRemove = (member: TeamMember) => {
    setSelectedMember(member)
    setRemoveMemberDialogOpen(true)
  }

  const handleConfirmRemove = async () => {
    if (!selectedMember) return
    return removeMember.mutateAsync(selectedMember.user_id)
  }

  const handleCancelInvitation = async (invitationId: string) => {
    setCancellingInvitationId(invitationId)
    try {
      await cancelInvitation.mutateAsync(invitationId)
    } finally {
      setCancellingInvitationId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Team Members</h1>
        <p className="text-muted-foreground">
          Manage your team and their access levels
        </p>
      </div>

      {isOwnerOrAdmin && (
        <div className="flex justify-end">
          <Button
            onClick={() => setAddMemberDialogOpen(true)}
            disabled={createTeamMember.isPending}
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Add Team Member
          </Button>
        </div>
      )}

      <Tabs defaultValue="members" className="w-full">
        <TabsList>
          <TabsTrigger value="members">
            Team Members ({members.length})
          </TabsTrigger>
          <TabsTrigger value="invitations">
            Pending Invitations ({invitations.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="space-y-4">
          <TeamMembersTable
            members={members}
            isOwner={isOwnerOrAdmin}
            currentUserId={user?.id}
            onEditRole={handleEditRole}
            onRemove={handleRemove}
            isLoading={membersLoading}
          />
        </TabsContent>

        <TabsContent value="invitations" className="space-y-4">
          <PendingInvitationsTable
            invitations={invitations}
            onCancel={handleCancelInvitation}
            isLoading={invitationsLoading}
            isCancelling={cancellingInvitationId}
          />
        </TabsContent>
      </Tabs>

      <AddTeamMemberDialog
        open={addMemberDialogOpen}
        onOpenChange={setAddMemberDialogOpen}
        onSubmit={handleCreateMember}
        isLoading={createTeamMember.isPending}
      />

      <EditMemberRoleDialog
        open={editRoleDialogOpen}
        onOpenChange={setEditRoleDialogOpen}
        member={selectedMember}
        onUpdateRole={handleUpdateRole}
        isLoading={updateMemberRole.isPending}
      />

      <RemoveMemberDialog
        open={removeMemberDialogOpen}
        onOpenChange={setRemoveMemberDialogOpen}
        member={selectedMember}
        onConfirm={handleConfirmRemove}
        isLoading={removeMember.isPending}
      />
    </div>
  )
}
